<template>
  <div class="px-8 pt-4 pb-2" :class="{ isEditing }">
    <v-row>
      <v-col cols="12" md="4">
        <text-field
          label="What type of assistance do you need? (Check all that apply)"
          v-model="services.type"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need for Rent/Mortgage?"
          v-model="services.rentMortgage"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need for Security Deposit?"
          v-model="services.securityDeposit"
          :rules="[validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need for Utilities?"
          v-model="services.utilities"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need for Car Assistance?"
          v-model="services.carAssistance"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need for Transportation?"
          v-model="services.transportation"
          :rules="[validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need for Medical Expenses? "
          v-model="services.medicalExpenses"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need for Food?"
          v-model="services.food"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need for Childcare? "
          v-model="services.childCare"
          :rules="[validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <text-field
          label="How much do you need assistance with Other Service(s)?"
          v-model="services.other"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="Total Services Needed"
          v-model="services.total"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>      
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <text-field
          label="Are there any other services you would like us to consider, if so, please describe them here:"
          v-model="services.otherServices"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>      
      <v-col cols="12" md="4">
        <text-field
          label="Describe the event(s) that led to the current financial crisis."
          v-model="services.events"
          :rules="[validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
    </v-row>
   
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import formatters from '@/helpers/formatters'
import validators from '@/helpers/validators'
import { findValidatableElement } from '@/helpers/form'

import TextField from '@/components/form/TextField'
import SelectField from '@/components/form/SelectField'
import AdsFormatContactField from '@/components/app/AdsFormatContactField'
import MultiSelectField from '@/components/form/MultiSelectField'
import DateField from '@/components/form/DateField'
import ReadOnlyField from '@/components/form/ReadOnlyField'
import RadioOptions from '@/components/form/RadioOptions'
import { GENDER_OPTIONS,DATE_OF_BIRTH_MINIMUM_YEAR } from '@/constants'

function dateToIsoDate(date) {
  return date.toISOString ? date.toISOString().slice(0, 10) : null
}

export default {
  name: 'ServiceDetails',
  components: {
    TextField,
    SelectField,
    MultiSelectField,
    DateField,
    ReadOnlyField,
    RadioOptions,
    AdsFormatContactField
  },
  props: {
    readonly: {
      type: Boolean,
      defaiult: true
    }
  },
  data() {
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    const startOfThisYear = new Date()
    startOfThisYear.setDate(1)
    startOfThisYear.setMonth(0)

    const endOfNextYear = new Date()
    endOfNextYear.setMonth(11, 31) // zero based months!
    endOfNextYear.setFullYear(startOfThisYear.getFullYear() + 1)

    const thisYear = new Date().getFullYear()
    const minAttendedYear = thisYear -16
    const maxAttendedLastDate = `${thisYear.toString()}-12-31`

    const {
      required,
      noPastDate,
      noFutureDate,
      noFutureMonthAndYear,
      noMonthAndYearBefore16years,
      name,
      primaryClientMaxAge
    } = validators

    return {
      MONTH_YEAR_DATE_FORMAT: 'MM-YYYY',
      minBirthDate: `${thisYear - DATE_OF_BIRTH_MINIMUM_YEAR}-01-01`,
      sevenDaysAgo: dateToIsoDate(sevenDaysAgo),
      endOfNextYear: dateToIsoDate(endOfNextYear),
      boolYesNo: formatters.boolYesNo,
      validators: {
        required,
        noPastDate,
        noFutureDate,
        noFutureMonthAndYear,
        noMonthAndYearBefore16years,
        name,
        primaryClientMaxAge,
        maxStartDate: validators.getMaxFutureYearValidator(1),
        prevSchoolEndDate: toDate =>
          validators.dateOrder(
            this.previousSchool.prevSchoolStartDate,
            toDate
          ) || "Cannot be earlier than 'Attended from' date"
      },
      genderOptions: GENDER_OPTIONS,
      minAttendedStartDate: new Date(`${minAttendedYear}-01-01`).toISOString().slice(0, 10),
      maxAttendedStartDate: new Date().toISOString().slice(0, 10),
      maxAttendedLastDate
    }
  },
  computed: {
    ...mapGetters([
      'application', 
      'isEditing', 
      'referenceData'
    ]),
    services() {
      return this.application.application.services
    }
  },
  methods: {
    
  }
}
</script>
